import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export type ToastVariant = 'success' | 'error' | 'warning' | 'info'

export interface ToastAction {
  label: string
  onClick: () => void
}

export interface ToastConfig {
  id: string
  message: string
  variant: ToastVariant
  duration?: number
  action?: ToastAction
  dismissible?: boolean
  createdAt: number
}

export interface UiState {
  sidebarOpen: boolean
  theme: 'light' | 'dark'
  loading: boolean
  notifications: Notification[]
  toasts: ToastConfig[]
}

export interface Notification {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  message: string
  timestamp: Date
}

// Load sidebar state from localStorage
const loadSidebarState = (): boolean => {
  try {
    const saved = localStorage.getItem('sidebar-open')
    if (saved === null || saved === 'undefined') {
      return true
    }
    return JSON.parse(saved)
  } catch (error) {
    console.warn('Failed to parse sidebar state from localStorage:', error)
    return true
  }
}

// Load theme preference from localStorage
const loadThemePreference = (): 'light' | 'dark' => {
  try {
    const saved = localStorage.getItem('theme-mode')
    if (saved === 'dark' || saved === 'light') {
      return saved
    }
    return 'light'
  } catch (error) {
    console.warn('Failed to parse theme preference from localStorage:', error)
    return 'light'
  }
}

// Validate and sanitize state to prevent corruption
const validateState = (state: Partial<UiState>): UiState => {
  return {
    sidebarOpen: typeof state.sidebarOpen === 'boolean' ? state.sidebarOpen : true,
    theme: state.theme === 'dark' || state.theme === 'light' ? state.theme : 'light',
    loading: typeof state.loading === 'boolean' ? state.loading : false,
    notifications: Array.isArray(state.notifications) ? state.notifications : [],
    toasts: Array.isArray(state.toasts) ? state.toasts : [],
  }
}

const initialState: UiState = validateState({
  sidebarOpen: loadSidebarState(),
  theme: loadThemePreference(),
  loading: false,
  notifications: [],
  toasts: [],
})

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen
      localStorage.setItem('sidebar-open', JSON.stringify(state.sidebarOpen))
    },
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload
      localStorage.setItem('sidebar-open', JSON.stringify(state.sidebarOpen))
    },
    setTheme: (state, action: PayloadAction<'light' | 'dark'>) => {
      state.theme = action.payload
      localStorage.setItem('theme-mode', action.payload)
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    addNotification: (state, action: PayloadAction<Omit<Notification, 'id' | 'timestamp'>>) => {
      const notification: Notification = {
        ...action.payload,
        id: Date.now().toString(),
        timestamp: new Date(),
      }
      // Ensure notifications array exists
      if (!Array.isArray(state.notifications)) {
        state.notifications = []
      }
      state.notifications.push(notification)
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      // Ensure notifications array exists
      if (!Array.isArray(state.notifications)) {
        state.notifications = []
        return
      }
      state.notifications = state.notifications.filter(n => n.id !== action.payload)
    },
    clearNotifications: (state) => {
      state.notifications = []
    },
    showToast: (state, action: PayloadAction<Omit<ToastConfig, 'id' | 'createdAt'>>) => {
      const toast: ToastConfig = {
        ...action.payload,
        id: `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        createdAt: Date.now(),
        dismissible: action.payload.dismissible ?? (action.payload.variant !== 'error'),
      }
      // Ensure toasts array exists
      if (!Array.isArray(state.toasts)) {
        state.toasts = []
      }
      state.toasts.push(toast)
      // Keep only the last 3 toasts
      if (state.toasts.length > 3) {
        state.toasts = state.toasts.slice(-3)
      }
    },
    dismissToast: (state, action: PayloadAction<string>) => {
      // Ensure toasts array exists
      if (!Array.isArray(state.toasts)) {
        state.toasts = []
        return
      }
      state.toasts = state.toasts.filter(t => t.id !== action.payload)
    },
    clearToasts: (state) => {
      state.toasts = []
    },
  },
})

export const {
  toggleSidebar,
  setSidebarOpen,
  setTheme,
  setLoading,
  addNotification,
  removeNotification,
  clearNotifications,
  showToast,
  dismissToast,
  clearToasts,
} = uiSlice.actions

export default uiSlice.reducer